import {
  type AchLimits,
  type AchLimitsError,
  BankAccountStatus,
  type CreateAchOrderErrorType,
  type CreateBankInternalTransferErrorType,
  CreateBankInvestmentTransferErrorType,
  type CreateInvestmentAchTransferErrorType,
  type Money,
  useCustomerDetails,
  useWealthOverview,
} from '@egw/api'
import { type FeatureFlags } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useFlags } from '@egw/feature-flags'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { yupResolver } from '@hookform/resolvers/yup'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { type FieldValues, type Resolver, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { nullTransferFieldOptionv2, TO_KEY } from '../constants'
import {
  BankInvestmentTransferValidationError,
  type EBackendError,
  EModalType,
  ETransferType,
  TransferField,
  type TransferFormInterface,
  type TransferListOption,
  type TransferSuccessTypes,
  type TResetFieldOption,
  type TSelectOption,
} from '../types'
import {
  buildAchOrderV2Payload,
  buildAllInternalOptions,
  buildBankInvestmentPayload,
  buildInternalBankTransferPayload,
  buildInvestmentAchPayload,
  buildLinkedAchAccounts,
  buildMoneyFromAmountField,
  buildTransferValidationSchema,
  checkIfExternalAccountsExist,
  checkOrderType,
  setMinAmountError,
  useGetDisplayValue,
} from '../utils/transfer-utils/transfer-utils'
import { useAccountBasedBalance } from './account-based-balance'
import { useAccountTransferLimits } from './account-transfer-limits'
import { useInvestmentAchTransfer } from './ach-investment-transfer'
import { useAchTransfer } from './ach-transfer'
import { useBankInvestmentTransfer } from './bank-investment-transfer'
import { useBankInvestmentTransferValidation } from './bank-investment-validation'
import { useInternalBankTransfer } from './internal-bank-transfer'

const log = new Logger('Transfer Context Hook')

export const useTransfer = () => {
  const { t } = useTranslation('features-transfer')

  const { flags } = useFlags<FeatureFlags>()

  // Balance that is available for a Bank | Investment Account to transfer money from
  const [fetchedAvailableBalance, setFetchedAvailableBalance] = useState<Money | undefined>()

  // Account Limits for the selected Account (HYC | Ach Account)
  const [fetchedTransferLimits, setFetchedTransferLimits] = useState<AchLimits | AchLimitsError | undefined>()

  // Page Layout State that handles showing of Success Message
  // ToFieldOption is used to display the product name in the success message
  const [displaySuccess, setDisplaySuccess] = useState<
    { successType: TransferSuccessTypes; toFieldOption: TransferListOption } | undefined
  >()

  // Page Layout State that handles showing of Error Message, separated as some error types overlap
  const [achError, setAchError] = useState<
    CreateAchOrderErrorType | EBackendError | 'BankingMinimumDepositNotMetError' | undefined
  >()
  const [bankInvestmentError, setBankInvestmentError] = useState<
    CreateBankInvestmentTransferErrorType | EBackendError | undefined
  >()
  const [internalBankTransferError, setInternalBankTransferError] = useState<
    CreateBankInternalTransferErrorType | EBackendError | undefined
  >()

  const [investmentAchError, setInvestmentAchError] = useState<
    CreateInvestmentAchTransferErrorType | 'AnnualRetirementContributionExceededError' | EBackendError | undefined
  >()

  const [bankInvestmentValidationError, setBankInvestmentValidationError] =
    useState<BankInvestmentTransferValidationError>(BankInvestmentTransferValidationError.NA)

  const [transferOptionsDrawerOpen, setTransferOptionsDrawerOpen] = useState<EModalType | undefined>(undefined)

  const clearTransferErrors = useCallback(() => {
    if (achError) setAchError(undefined)
    if (bankInvestmentError) setBankInvestmentError(undefined)
    if (internalBankTransferError) setInternalBankTransferError(undefined)
    if (investmentAchError) setInvestmentAchError(undefined)
  }, [achError, bankInvestmentError, internalBankTransferError, investmentAchError])

  // -- REACT HOOK FORM VALUES ---------------------------------------------------------------
  const formMethods = useForm<TransferFormInterface>({
    mode: 'all',
    defaultValues: {
      [TransferField.From]: nullTransferFieldOptionv2,
      [TransferField.To]: nullTransferFieldOptionv2,
      [TransferField.Amount]: '',
    },
    resolver: yupResolver(
      yup.object().shape(
        buildTransferValidationSchema({
          t,
          availableBalanceAmount: fetchedAvailableBalance?.amount ?? 0,
          min100BrokerageDeposit: flags.rolloutMin100DepositBrokerage,
        }),
      ),
    ) as unknown as Resolver<TransferFormInterface>,
  })

  const submitForm = formMethods.handleSubmit(transferFunds)

  // Watch Field Values from React Hook Form
  const fromFieldOption = formMethods.watch(TransferField.From)
  const toFieldOption = formMethods.watch(TransferField.To)
  const amountFieldValue = formMethods.watch(TransferField.Amount)

  const selectOption = useCallback(
    ({ option, key }: TSelectOption) => {
      formMethods.setValue(key, option)
    },
    [formMethods],
  )

  const resetFieldOption = useCallback(
    ({ key }: TResetFieldOption) => {
      if (key === TransferField.From || key === TransferField.To) {
        selectOption({ key, option: nullTransferFieldOptionv2 })
        formMethods.clearErrors(key)
      }

      if (key === TransferField.Amount) {
        formMethods.setValue(key, '', { shouldValidate: false })
        formMethods.clearErrors(TransferField.Amount)
      }
    },
    [formMethods, selectOption],
  )

  const isFormValid = useMemo(
    () =>
      Boolean(
        formMethods.formState.isValid && !Object.keys(formMethods.formState.errors).length && toFieldOption.value,
      ),
    [formMethods.formState.isValid, formMethods.formState.errors, toFieldOption.value],
  )

  const { reset: resetForm, setError: onInlineError } = formMethods

  const transferType = useMemo(
    () => checkOrderType({ fromFieldOption, toFieldOption }),
    [fromFieldOption, toFieldOption],
  )

  const openFromModal = useCallback(() => {
    log.info('Opening From Modal')
    setTransferOptionsDrawerOpen(EModalType.From)
  }, [])

  const openToModal = useCallback((): void => {
    setTransferOptionsDrawerOpen(EModalType.To)
  }, [])

  const closeTransferOptionsDrawer = useCallback(() => {
    setTransferOptionsDrawerOpen(undefined)
  }, [])

  // -- QUERIES || MUTATIONS ---------------------------------------------------------------
  const {
    data: accountDataResponse,
    isLoading: customerDetailsLoading,
    isError: customerDetailsError,
  } = useCustomerDetails()

  const {
    data: wealthOverviewData,
    isLoading: wealthOverviewLoading,
    isError: wealthOverviewError,
    refetch: refetchAccountData,
  } = useWealthOverview()

  const accountData = useMemo(() => {
    const internalAccounts = buildAllInternalOptions(wealthOverviewData, t, flags.rolloutDisableIraTransfers)
    const externalAccounts = buildLinkedAchAccounts(accountDataResponse?.customer.linkedAccounts.achAccounts)
    return {
      allFieldOptions: [...internalAccounts, ...externalAccounts],
      tags: accountDataResponse?.customer.tags ?? [],
      externalAccountsExist: accountDataResponse && checkIfExternalAccountsExist(accountDataResponse),
      checkingAccountAlias: wealthOverviewData?.highYieldChecking?.accounts[0]?.alias,
      checkingAccountClosed: wealthOverviewData?.highYieldChecking?.accounts[0]?.status === BankAccountStatus.CLOSED,
      checkingAccountProductName: wealthOverviewData?.highYieldChecking?.productName,
      investmentsProductName: wealthOverviewData?.personalPortfolio?.productName,
    }
  }, [accountDataResponse, wealthOverviewData, t, flags.rolloutDisableIraTransfers])

  const allFieldOptions = accountData.allFieldOptions

  useEffect(() => {
    // Refetch Account Data on Successful Transfer
    if (displaySuccess) {
      sendEvent('eg_transferFunds_accept', {
        eventSource: 'egWealth',
        category: 'transfer',
        subCategory: 'result',
        transferType,
        fromAccountType: fromFieldOption.accountType,
        toAccountType: toFieldOption.accountType,
        fromAccount: fromFieldOption.display.name,
        toAccount: toFieldOption.display.name,
        amount: amountFieldValue,
        errorType: achError ?? bankInvestmentError ?? internalBankTransferError ?? investmentAchError,
      })
      const refetchHooks = async (): Promise<void> => {
        await refetchAccountData().catch((e: unknown) => {
          log.error(e)
        })
      }
      refetchHooks().catch((e: unknown) => {
        log.error(e)
      })
    }
  }, [
    achError,
    amountFieldValue,
    bankInvestmentError,
    investmentAchError,
    displaySuccess,
    fromFieldOption.accountType,
    fromFieldOption.display.name,
    internalBankTransferError,
    refetchAccountData,
    toFieldOption.accountType,
    toFieldOption.display.name,
    transferType,
  ])

  useEffect(() => {
    if (
      achError ??
      bankInvestmentError ??
      internalBankTransferError ??
      investmentAchError ??
      bankInvestmentValidationError !== BankInvestmentTransferValidationError.NA
    ) {
      sendEvent('eg_transferFunds_reject', {
        eventSource: 'egWealth',
        category: 'transfer',
        subCategory: 'result',
        transferType,
        fromAccountType: fromFieldOption.accountType,
        toAccountType: toFieldOption.accountType,
        fromAccount: fromFieldOption.display.name,
        toAccount: toFieldOption.display.name,
        amount: amountFieldValue,
        errorType:
          achError ??
          bankInvestmentError ??
          internalBankTransferError ??
          investmentAchError ??
          bankInvestmentValidationError,
      })
    }
  }, [
    achError,
    bankInvestmentError,
    internalBankTransferError,
    investmentAchError,
    bankInvestmentValidationError,
    amountFieldValue,
    fromFieldOption,
    toFieldOption,
    transferType,
  ])

  // Create Display Value from Form Selection
  const fromFieldDisplayValue = useGetDisplayValue({
    fieldOption: fromFieldOption,
    allFieldOptions,
    isModalOpen: false,
    t,
  })

  const toFieldDisplayValue = useGetDisplayValue({
    fieldOption: toFieldOption,
    allFieldOptions,
    isModalOpen: false,
    t,
  })

  // Hoists Balance Fetched to state to drive Validation Schema for React Hook Form
  // TODO FIX THIS
  const { isFetching: fetchingAccountBalance } = useAccountBasedBalance({
    alias: fromFieldOption.alias,
    accountType: fromFieldOption.accountType,
    onSettledCb: setFetchedAvailableBalance,
  })

  // Hoists Account Transfer Limits to state
  useAccountTransferLimits({
    alias: fromFieldOption.alias,
    accountType: fromFieldOption.accountType,
    onSettledCb: setFetchedTransferLimits,
  })

  const onSuccess = useCallback(
    (successType: TransferSuccessTypes) => {
      // ToFieldOption is used to display the product name in the success message
      setDisplaySuccess({ successType, toFieldOption })
      resetForm()
      resetFieldOption({ key: TO_KEY })
    },
    [resetForm, resetFieldOption, toFieldOption],
  )

  const { createAchOrderMutation, achOrderIsLoading } = useAchTransfer({
    onSuccess,
    onError: setAchError,
    onInlineError,
  })

  useBankInvestmentTransferValidation({
    fromFieldOption,
    toFieldOption,
    transferType,
    bankInvestmentValidationError,
    setBankInvestmentValidationError,
    onInlineError,
    clearInlineError: formMethods.clearErrors,
  })

  const { createBankInvestmentTransferMutation, bankInvestmentTransferLoading } = useBankInvestmentTransfer({
    onSuccess,
    onError: setBankInvestmentError,
    onInlineError,
  })

  const { createInternalBankTransferMutation, internalBankTransferLoading } = useInternalBankTransfer({
    onSuccess,
    onError: setInternalBankTransferError,
    onInlineError,
  })

  const { createInvestmentAchTransferMutation, investmentAchTransferLoading } = useInvestmentAchTransfer({
    onSuccess,
    onError: setInvestmentAchError,
    onInlineError,
  })

  // Passed to the onSubmit of the form hook
  function transferFunds({
    [TransferField.From]: fromPosition,
    [TransferField.To]: toPosition,
    [TransferField.Amount]: amount,
  }: FieldValues): void {
    const money = buildMoneyFromAmountField(amount as string)

    if (money.amount <= 0) {
      setMinAmountError({ t, setError: formMethods.setError, money })
      return
    }

    const payload = {
      fromPosition: fromPosition as TransferListOption,
      toPosition: toPosition as TransferListOption,
      amount: amount as string,
    }

    log.info('Transfer Funds', {
      payload,
      transferType,
    })

    switch (transferType) {
      case ETransferType.ACH: {
        const achPayload = buildAchOrderV2Payload(payload)
        createAchOrderMutation(achPayload).catch((e: unknown) => {
          log.error(e)
        })
        break
      }

      case ETransferType.BankInvestment: {
        createBankInvestmentTransferMutation(
          buildBankInvestmentPayload({
            ...payload,
            liquidateAccount: Boolean(
              bankInvestmentError &&
              bankInvestmentError === CreateBankInvestmentTransferErrorType.AMOUNT_REQUESTED_REQUIRES_LIQUIDATION,
            ),
          }),
        ).catch((e: unknown) => {
          log.error(e)
        })
        break
      }

      case ETransferType.BookTransfer: {
        createInternalBankTransferMutation(buildInternalBankTransferPayload(payload)).catch((e: unknown) => {
          log.error(e)
        })
        break
      }

      case ETransferType.InvestmentAch: {
        createInvestmentAchTransferMutation(buildInvestmentAchPayload(payload)).catch((e: unknown) => {
          log.error(e)
        })
        break
      }

      case ETransferType.NA:
      default: {
        log.error('Invalid transfer type', { transferType })
        break
      }
    }

    sendEvent('egw_transfer_submit', {
      eventSource: 'egWealth',
      category: 'transfer',
      subCategory: 'intent',
      transferType,
      fromAccountType: payload.fromPosition.accountType,
      toAccountType: payload.toPosition.accountType,
      fromAccount: payload.fromPosition.display.name,
      toAccount: payload.toPosition.display.name,
      amount: payload.amount,
    })
  }

  return {
    allFieldOptions,
    accountData,
    isAccountDataLoading: customerDetailsLoading || wealthOverviewLoading,
    isAccountDataError: customerDetailsError || wealthOverviewError,
    fetchedAvailableBalance,
    fetchingAccountBalance,
    fetchedTransferLimits,
    displaySuccess,
    transferType,
    setDisplaySuccess,
    formMethods,
    fromFieldOption,
    fromFieldDisplayValue,
    toFieldOption,
    toFieldDisplayValue,
    amountFieldValue,
    isFormValid,
    submitForm,
    openFromModal,
    openToModal,
    createAchOrderMutation,
    achOrderIsLoading,
    createBankInvestmentTransferMutation,
    bankInvestmentTransferLoading,
    createInternalBankTransferMutation,
    internalBankTransferLoading,
    createInvestmentAchTransferMutation,
    investmentAchTransferLoading,
    achError,
    bankInvestmentError,
    internalBankTransferError,
    investmentAchError,
    clearTransferErrors,
    selectOption,
    resetFieldOption,
    transferOptionsDrawerOpen,
    closeTransferOptionsDrawer,
    bankInvestmentValidationError,
  }
}
